import { useMutation } from "@tanstack/react-query";
import type { ProjectView, SetProjectCustomersRequest } from "@portal/schemas";
import {
  Button,
  Checkbox,
  Input,
  NativeSelect,
  NativeSelectOption,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@portal/ui";
import { useState } from "react";
import { setProjectCustomers } from "../../api/client";
import { useCustomers, useInvalidate } from "../../api/queries";
import { DialogTitleWithHint } from "../../components/Hint";
import { HomeSection } from "../../components/HomeSection";
import { InlineError } from "../../components/InlineError";
import { CREATE_NEW } from "../../components/select-create";
import { CustomerFormDialog } from "../customers/CustomerFormDialog";
import { CUSTOMER_RELATION_LABELS } from "./ProjectStatusBadge";
import { Pencil, Save, X } from "lucide-react";

type Row = SetProjectCustomersRequest["customers"][number];

export function ProjectCustomersCard({ project }: { project: ProjectView }) {
  return (
    <HomeSection
      data-testid="project-customers-card"
      title="顧客"
      description="このプロジェクトに関係する顧客"
      action={project.canEdit && <EditDialog project={project} />}
    >
      {project.customers.length === 0 && (
        <p className="text-sm text-muted-foreground">顧客は登録されていません。</p>
      )}
      <ul className="flex flex-col divide-y text-sm">
        {project.customers.map((c) => (
          <li key={c.customerId} className="flex items-center justify-between py-2">
            <span>
              {c.customerName}
              {c.isPrimary && <span className="ml-2 text-xs text-muted-foreground">主顧客</span>}
            </span>
            <span className="text-xs text-muted-foreground">
              {CUSTOMER_RELATION_LABELS[c.relation]}
            </span>
          </li>
        ))}
      </ul>
    </HomeSection>
  );
}

function EditDialog({ project }: { project: ProjectView }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>(() =>
    project.customers.map((c) => ({
      customerId: c.customerId,
      relation: c.relation,
      isPrimary: c.isPrimary,
      note: c.note,
    })),
  );
  const customers = useCustomers("ACTIVE");
  const invalidate = useInvalidate();
  const save = useMutation({
    mutationFn: () =>
      setProjectCustomers(project.id, { revision: project.revision, customers: rows }),
    onSuccess: async () => {
      await invalidate.projects();
      setOpen(false);
    },
  });
  const available = (customers.data?.customers ?? []).filter(
    (c) => !rows.some((r) => r.customerId === c.id),
  );
  const nameOf = (id: string) =>
    customers.data?.customers.find((c) => c.id === id)?.name ??
    project.customers.find((c) => c.customerId === id)?.customerName ??
    id;
  // 「＋ 新しい顧客を登録…」で開く登録のダイアログ
  const [creating, setCreating] = useState(false);
  const addRow = (customerId: string) =>
    setRows((rs) =>
      rs.some((r) => r.customerId === customerId)
        ? rs
        : [...rs, { customerId, relation: "CLIENT", isPrimary: rs.length === 0, note: "" }],
    );
  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) =>
      rs.map((r, j) =>
        j === i ? { ...r, ...patch } : patch.isPrimary ? { ...r, isPrimary: false } : r,
      ),
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o)
          setRows(
            project.customers.map((c) => ({
              customerId: c.customerId,
              relation: c.relation,
              isPrimary: c.isPrimary,
              note: c.note,
            })),
          );
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="編集" title="編集">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitleWithHint hint="主顧客は 1 件までです。顧客マスタに無い顧客は「＋ 新しい顧客を登録…」から登録できます。">
            顧客を編集
          </DialogTitleWithHint>
        </DialogHeader>
        <div className="flex flex-col gap-3 overflow-y-auto px-4">
          {rows.map((r, i) => (
            <div key={r.customerId} className="flex flex-col gap-2 rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">{nameOf(r.customerId)}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  aria-label="外す"
                  title="外す"
                >
                  <X />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <NativeSelect
                  aria-label="関係種別"
                  value={r.relation}
                  onChange={(e) => update(i, { relation: e.target.value as Row["relation"] })}
                >
                  {Object.entries(CUSTOMER_RELATION_LABELS).map(([k, v]) => (
                    <NativeSelectOption key={k} value={k}>
                      {v}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={r.isPrimary}
                    onCheckedChange={(v) => update(i, { isPrimary: v === true })}
                  />
                  主顧客
                </label>
              </div>
              <Input
                placeholder="備考"
                value={r.note}
                onChange={(e) => update(i, { note: e.target.value })}
              />
            </div>
          ))}
          <NativeSelect
            aria-label="顧客を追加"
            value=""
            onChange={(e) => {
              if (e.target.value === CREATE_NEW) setCreating(true);
              else if (e.target.value) addRow(e.target.value);
            }}
          >
            <NativeSelectOption value="">顧客を追加…</NativeSelectOption>
            {available.map((c) => (
              <NativeSelectOption key={c.id} value={c.id}>
                {c.name}
              </NativeSelectOption>
            ))}
            {/* マスタに無い顧客は、ここから登録してそのまま追加できる(ユーザー要望 2026-10-15) */}
            <NativeSelectOption value={CREATE_NEW}>＋ 新しい顧客を登録…</NativeSelectOption>
          </NativeSelect>
          <CustomerFormDialog
            open={creating}
            onOpenChange={setCreating}
            onSaved={(c) => addRow(c.id)}
          />
          <InlineError error={save.error ?? customers.error} />
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            <Save />
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
